import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { clone, compact, find, filter, isEmpty, map, noop } from 'underscore';
import { DefaultButton, Panel, PanelType, PrimaryButton } from '@fluentui/react';
import { AudienceAssociationView } from '@bingads-webui-campaign-legacy/audience-association/view/base-association-view';
import { AssociationTypes } from '@bingads-webui-campaign-legacy/audience-association/model/consts';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withUiBlock } from '@bingads-webui/uiblocker';

import { getAudiencePickerStyle } from '../styles/audience-picker';
import { withoutEasyDismiss } from './without-easy-dismiss';

const UnstyledAudiencePicker = (props) => {
  const {
    classes,
    i18n,
    isOpen,
    title,
    description,
    audienceType,
    audiences,
    onAudiencesChanged,
    onClose,
    onDismiss,
    perfMarker,
  } = props;

  const [audienceList, setAudienceList] = useState(clone(audiences));

  const [audienceAssociationRef, setAudienceAssociationRef] = useState();

  const audienceAssociationView = useMemo(() => {
    const associationView = new AudienceAssociationView({
      scope: {}, // this is needed to request audiences for all delivery channels
      campaignType: null, // this is needed to request audiences for all delivery channels
      associationType: AssociationTypes.Aim, // this is needed to remove all non-aim sections
      isFromPerformanceMaxCampaign: true,
      isNewUI: true,
      refreshOnInitialize: true,
      perfMarker,
    });

    associationView.canExpandAssociation = true;
    associationView.render();
    associationView.expandAssociation();

    return associationView;
  }, [perfMarker]);

  const onAdd = useCallback(() => {
    onAudiencesChanged(map(audienceList, audience => ({
      ...audience,
      Name: audience.Name || audience.name,
    })));

    onClose();
  }, [audienceList, onAudiencesChanged, onClose]);

  const onAudienceListChanged = useCallback(() => {
    const { associationList } = audienceAssociationView.getLists();
    setAudienceList(associationList);
  }, [audienceAssociationView]);

  const onRenderFooterContent = useCallback(() => (
    <div className="audience-picker-buttons">
      <PrimaryButton className="btn-add" onClick={onAdd}>
        {i18n.getString(_TL_('Add'))}
      </PrimaryButton>
      <DefaultButton className="btn-cancel" onClick={onClose}>
        {i18n.getString(_TL_('Cancel'))}
      </DefaultButton>
    </div>
  ), [i18n, onAdd, onClose]);

  useEffect(() => {
    if (audienceAssociationRef) {
      if (audienceType === 'InMarket') {
        const audienceOptions = filter(audienceAssociationView.doubleListBoxViewModel.configSettingsOptions(), ({ value }) => value === 'InMarket');
        audienceAssociationView.doubleListBoxViewModel.configSettingsOptions(audienceOptions);
        audienceAssociationView.doubleListBoxViewModel.setSelectedCategory('InMarket');
      } else if (audienceType === 'CustomerList') {
        const audienceOptions = filter(audienceAssociationView.doubleListBoxViewModel.configSettingsOptions(), ({ value }) => value === 'CustomerList');
        audienceAssociationView.doubleListBoxViewModel.configSettingsOptions(audienceOptions);
        audienceAssociationView.doubleListBoxViewModel.setSelectedCategory('CustomerList');
      } else {
        const audienceOptions = filter(audienceAssociationView.doubleListBoxViewModel.configSettingsOptions(), ({ value }) => value !== 'InMarket');
        audienceAssociationView.doubleListBoxViewModel.configSettingsOptions(audienceOptions);
      }
      audienceAssociationRef.append(audienceAssociationView.el);

      audienceAssociationView
        .ensureLoadedAsync()
        .then(() => {
          const associationList = compact(map(audienceList, ({ Name }) => find(audienceAssociationView.doubleListBoxViewModel.allAudiences, audience =>
            (audience.Name || audience.name) === Name)));

          audienceAssociationView.setLists({
            associationList,
            exclusionList: [],
          });

          if (!isEmpty(associationList)) {
            audienceAssociationView.doubleListBoxViewModel.setSelectedCategory(associationList[0].Type);
          }

          audienceAssociationView.on('selectionChanged', onAudienceListChanged);
        })
        .finally(withUiBlock(audienceAssociationRef));

      return () => {
        audienceAssociationView.off('selectionChanged', onAudienceListChanged);
      };
    }

    return noop;
  }, [audienceAssociationRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Panel
      aria-modal="true"
      className={classNames(['audience-picker', classes.pickerContainer])}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      headerText={title}
      onDismiss={onDismiss}
      onRenderFooterContent={onRenderFooterContent}
      isOpen={isOpen}
      isFooterAtBottom
      type={PanelType.custom}
      customWidth="970px"
    >
      <div className="audience-description">{description}</div>
      <div className={classNames(['audience-association-view', audienceType])} ref={newRef => setAudienceAssociationRef(newRef)} />
    </Panel>
  );
};

UnstyledAudiencePicker.propTypes = {
  classes: PropTypes.shape({
    pickerContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isOpen: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  audienceType: PropTypes.string,
  audiences: PropTypes.arrayOf(PropTypes.shape({})),
  onAudiencesChanged: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
};

UnstyledAudiencePicker.defaultProps = {
  isOpen: false,
  audienceType: undefined,
  audiences: [],
  onAudiencesChanged: noop,
};

export const AudiencePicker = withDefaultStyles(withoutEasyDismiss(UnstyledAudiencePicker), getAudiencePickerStyle);
